import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import AIChatWidget from "@/components/AIChatWidget";
import {
  LayoutDashboard, Package, Calculator, FileText, BookOpen, ArrowDownUp,
  TrendingUp, Settings, Users, ClipboardList, AlertTriangle, Brain,
  ChevronDown, LogOut, Menu, X, Building2, Shield, Upload, Receipt, AlertCircle, Heart, Rocket, FileCheck, Layers, Briefcase, ArrowRightLeft, Key, BookMarked, CreditCard, Headphones
} from "lucide-react";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tier: 1 },
  { path: "/health-score", label: "Health Score", icon: Heart, tier: 1 },
  { path: "/guide", label: "How to Use", icon: BookMarked, tier: 1 },
  { path: "/onboarding", label: "Setup Wizard", icon: Rocket, tier: 1 },
  { path: "/assets", label: "Asset Register", icon: Package, tier: 1 },
  { path: "/import", label: "Import Assets", icon: Upload, tier: 1 },
  { path: "/depreciation", label: "Depreciation", icon: Calculator, tier: 1 },
  { path: "/tax-depreciation", label: "Tax & Deferred Tax", icon: Receipt, tier: 1 },
  { path: "/journals", label: "Journals", icon: BookOpen, tier: 1 },
  { path: "/disposals", label: "Disposals", icon: ArrowDownUp, tier: 1 },
  { path: "/far", label: "Full FAR (AFS Note)", icon: FileText, tier: 1 },
  { path: "/leases", label: "IFRS 16 Leases", icon: Key, tier: 2 },
  { path: "/impairment", label: "IAS 36 Impairment", icon: AlertTriangle, tier: 2 },
  { path: "/reclassification", label: "Reclassification", icon: ArrowRightLeft, tier: 2 },
  { path: "/anomalies", label: "Anomaly Alerts", icon: AlertCircle, tier: 2 },
  { path: "/capex", label: "Capex / WIP", icon: TrendingUp, tier: 2 },
  { path: "/policies", label: "Category Policies", icon: ClipboardList, tier: 2 },
  { path: "/industry-modules", label: "Industry Modules", icon: Layers, tier: 2 },
  { path: "/reassessments", label: "Reassessments", icon: AlertTriangle, tier: 2 },
  { path: "/suggestions", label: "AI Suggestions", icon: Brain, tier: 2 },
  { path: "/policy-document", label: "Policy Document", icon: FileCheck, tier: 2 },
  { path: "/board-pack", label: "Board Pack", icon: Briefcase, tier: 2 },
  { path: "/scenarios", label: "What-If Modeller", icon: Calculator, tier: 2 },
  { path: "/auditor-pack", label: "Auditor Pack", icon: Shield, tier: 2 },
  { path: "/subscription", label: "Subscription", icon: CreditCard, tier: 3 },
  { path: "/support", label: "Get Assistance", icon: Headphones, tier: 3 },
  { path: "/security", label: "Security & POPIA", icon: Shield, tier: 3 },
  { path: "/users", label: "Users", icon: Users, tier: 3 },
  { path: "/settings", label: "Settings", icon: Settings, tier: 3 },
  { path: "/audit-log", label: "Audit Log", icon: Shield, tier: 3 },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { entities, activeEntity, switchEntity } = useApp();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [entityDropdown, setEntityDropdown] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const visibleNav = NAV_ITEMS.filter((item) => {
    if (user?.role === "auditor") return item.tier <= 2;
    if (user?.role === "accountant") return item.tier <= 1 || item.path === "/journals";
    if (user?.role === "data_entry") return item.tier === 1;
    return true;
  });

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F5EF]" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? "w-64" : "w-16"} ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} fixed md:relative z-40 h-full bg-[#0B1020] text-white transition-all duration-300 flex flex-col`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 flex items-center gap-3 border-b border-white/10">
          <img
            src="/assets/stratum_logomark.png"
            alt="Stratum"
            className="w-8 h-8 rounded-lg"
          />
          {sidebarOpen && (
            <div>
              <h1 className="font-display text-lg font-semibold tracking-tight">Stratum</h1>
              <p className="text-[10px] text-white/50 tracking-widest uppercase">Asset Register</p>
            </div>
          )}
        </div>

        {/* Entity Switcher */}
        {sidebarOpen && (
          <div className="p-3 border-b border-white/10">
            <div
              className="relative"
              data-testid="entity-switcher"
            >
              <button
                onClick={() => setEntityDropdown(!entityDropdown)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-sm"
                data-testid="entity-switcher-btn"
              >
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-[#C8A961]" />
                  <span className="truncate">{activeEntity?.name || "All Entities (Consolidated)"}</span>
                </div>
                <ChevronDown size={14} className={`transition-transform ${entityDropdown ? "rotate-180" : ""}`} />
              </button>
              {entityDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#1A233A] rounded-lg border border-white/10 shadow-xl z-50 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => { switchEntity(null); setEntityDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors rounded-t-lg ${!activeEntity ? "bg-white/10 text-[#C8A961]" : ""}`}
                    data-testid="entity-option-ALL"
                  >
                    <div className="font-medium">All Entities (Consolidated)</div>
                    <div className="text-xs text-white/40">Group-wide view</div>
                  </button>
                  {entities.map((ent) => (
                    <button
                      key={ent.id}
                      onClick={() => { switchEntity(ent); setEntityDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors last:rounded-b-lg ${activeEntity?.id === ent.id ? "bg-white/10 text-[#C8A961]" : ""}`}
                      data-testid={`entity-option-${ent.code}`}
                    >
                      <div className="font-medium">{ent.name}</div>
                      <div className="text-xs text-white/40">{ent.code} - {ent.role}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-[#C8A961]/20 text-[#C8A961] font-medium"
                    : "text-white/60 hover:bg-white/5 hover:text-white/90"
                }`}
                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
              >
                <Icon size={18} strokeWidth={1.5} />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-white/10">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-white/40 truncate capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
              <button
                onClick={logout}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/50 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button onClick={logout} className="p-2 rounded-lg hover:bg-white/10 w-full flex justify-center" data-testid="logout-btn">
              <LogOut size={16} className="text-white/50" />
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-14 bg-[#F8F5EF]/80 backdrop-blur-xl border-b border-[#E8E2D5] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSidebarOpen(!sidebarOpen); setMobileOpen(!mobileOpen); }}
              className="p-2 rounded-lg hover:bg-white/50 transition-colors"
              data-testid="toggle-sidebar-btn"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0B1020]">
                {activeEntity?.name || "Stratum"}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
            {user?.role === "auditor" && (
              <span className="px-2 py-1 rounded bg-[#1E40AF]/10 text-[#1E40AF] text-xs font-medium">Auditor View</span>
            )}
            <span className="hidden md:inline">{user?.email}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* AI Chat Widget */}
      <AIChatWidget />
    </div>
  );
}
